import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GetAppIcon from "@mui/icons-material/GetApp";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import AuthContext from "../../../context/AuthContext";
import { getBusinessReport } from "./BusinessReport.thunk";
import {
  formatUtcToLocal,
  getLocalTzLabel,
} from "../../../services/dateUtils";

const tzLabel = getLocalTzLabel();

const countryOptions = [
  { value: "", label: "Select" },
  { value: "US", label: "USA" },
  { value: "MX", label: "Mexico" },
  { value: "CO", label: "Colombia" },
  { value: "PE", label: "Peru" },
  { value: "BR", label: "Brazil" },
  { value: "CH", label: "Chile" },
  { value: "AR", label: "Argentina" },
  { value: "BO", label: "Bolivia" },
];

const toCSV = (nodes) => {
  const rows = [];

  const traverse = (items, parent = "") => {
    items.forEach((item) => {
      if (item.exchanges?.length) {
        item.exchanges.forEach((exchange) => {
          rows.push({
            ...item,
            product: exchange.exchange_code,
            total_transactions: exchange.total_transactions,
            parent,
          });
        });
      } else {
        rows.push({
          ...item,
          product: "",
          total_transactions: "",
          parent,
        });
      }
      if (item.entities?.length) traverse(item.entities, item.entity_name);
    });
  };

  traverse(nodes);

  const header = [
    "Entity Name",
    "Entity Type",
    "Country",
    "Product",
    "Commissions Fee %",
    tzLabel ? `Last Transaction (${tzLabel})` : "Last Transaction",
    "Total Transactions",
    "Parent",
  ];

  const csv = [header.join(",")];
  rows.forEach((row) => {
    csv.push(
      [
        row.entity_name,
        row.entity_type,
        row.country,
        row.product,
        row.fee_percent,
        formatUtcToLocal(row.last_transaction),
        row.total_transactions,
        row.parent,
      ]
        .map((value) => `"${value ?? ""}"`)
        .join(","),
    );
  });

  return csv.join("\n");
};

// UI: hora local del navegador (el back envía UTC).
const formatDate = (dateString) => formatUtcToLocal(dateString);

const BusinessReport = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const { entities, status, error } = useSelector(
    (state) => state.businessReport,
  );

  const [searchText, setSearchText] = useState("");
  const [country, setCountry] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchReport = useCallback(
    () => {
      dispatch(
        getBusinessReport({
          token,
          entityName: searchText,
          country,
          startDate,
          endDate,
        }),
      );
    },
    [dispatch, token, searchText, country, startDate, endDate],
  );

  useEffect(() => {
    if (token) {
      fetchReport();
    }
  }, [token, fetchReport]);

  const handleSearch = () => {
    fetchReport();
  };

  const handleExport = () => {
    const csvContent = toCSV(entities);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `business-report-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const renderRows = (nodes, level = 0) => {
    if (!nodes?.length) return [];

    const rows = [];
    nodes.forEach((node) => {
      const isChild = level > 0;
      let entityTypeBackground = "transparent";
      let entityTypeColor = "inherit";
      if (node.entity_type === "Acquirer") {
        entityTypeBackground = theme.palette.blokko.entity.acquirer.bg;
        entityTypeColor = theme.palette.blokko.entity.acquirer.text;
      } else if (node.entity_type === "ISO") {
        entityTypeBackground = theme.palette.blokko.entity.iso.bg;
        entityTypeColor = theme.palette.blokko.entity.iso.text;
      } else if (node.entity_type === "Merchant") {
        entityTypeBackground = theme.palette.blokko.entity.merchant.bg;
        entityTypeColor = theme.palette.blokko.entity.merchant.text;
      }

      const exchangeRows = node.exchanges?.length
        ? node.exchanges
        : [{ exchange_code: '', total_transactions: '' }];
      const rowSpan = exchangeRows.length;

      exchangeRows.forEach((exchange, index) => {
        const isFirstRow = index === 0;

        rows.push(
          <TableRow
            key={`${node.entity_id}-${exchange.exchange_code || index}`}
            sx={{
              backgroundColor: isChild
                ? theme.palette.blokko.tableRow
                : 'inherit',
              '&:hover': {
                backgroundColor: isChild
                  ? theme.palette.blokko.tableRowHover
                  : theme.palette.blokko.taglineMist,
              },
            }}
          >
            {isFirstRow && (
              <TableCell sx={{ pl: 2 + level * 3, minWidth: 250 }} rowSpan={rowSpan}>
                <Typography component="span" sx={{ fontWeight: 500 }}>
                  {node.entity_name}
                </Typography>
              </TableCell>
            )}
            {isFirstRow && (
              <TableCell sx={{ minWidth: 120 }} rowSpan={rowSpan}>
                <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    backgroundColor: entityTypeBackground,
                    color: entityTypeColor,
                  }}
                >
                  {node.entity_type}
                </Box>
              </TableCell>
            )}
            {isFirstRow && (
              <TableCell sx={{ minWidth: 100 }} rowSpan={rowSpan}>
                {node.country}
              </TableCell>
            )}
            {isFirstRow && (
              <TableCell sx={{ minWidth: 120 }} rowSpan={rowSpan}>
                {node.fee_percent}
              </TableCell>
            )}
            {isFirstRow && (
              <TableCell sx={{ minWidth: 140 }} rowSpan={rowSpan}>
                {formatDate(node.last_transaction)}
              </TableCell>
            )}
            <TableCell sx={{ minWidth: 120 }}>{exchange.exchange_code || '-'}</TableCell>
            <TableCell sx={{ minWidth: 120 }}>{exchange.total_transactions ?? '-'}</TableCell>
          </TableRow>,
        );
      });

      if (node.entities?.length) {
        rows.push(...renderRows(node.entities, level + 1));
      }
    });

    return rows;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Business Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600 }}>
            Search filters
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                label="Search by text"
                placeholder="Entity name"
                fullWidth
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Country"
                placeholder="Country"
                fullWidth
                size="small"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                select
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => value || "Country",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              >
                {countryOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={tzLabel ? `Start Date (${tzLabel})` : "Start Date"}
                type="date"
                placeholder="mm/dd/yyyy"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={tzLabel ? `End Date (${tzLabel})` : "End Date"}
                type="date"
                placeholder="mm/dd/yyyy"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-start" }}>
                <Button variant="contained" color="primary" onClick={handleSearch}>
                  Search
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  onClick={handleExport}
                >
                  Export to excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Results Table
            </Typography>
          </Box>

          {status === "loading" && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {status !== "loading" && (
            <TableContainer component={Paper} sx={{ maxHeight: 530 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        fontWeight: 700,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    <TableCell sx={{ minWidth: 250 }}>Entity Name</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Entity Type</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Country</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Commissions Fee %</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      {tzLabel ? `Last Transaction (${tzLabel})` : "Last Transaction"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Product</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Total Transactions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entities?.length ? (
                    renderRows(entities)
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {error
                          ? "Error loading data. Please try again."
                          : "No entities found for the selected filters."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BusinessReport;
